import { MutableRefObject, RefObject, useEffect, useRef, useState } from 'react';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { useWaveSurfer } from '../audio-wave/use-wave-surfer';

const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

type TRecordStatus = 'idle' | 'recording' | 'recorded';

interface IRecordAudioWaveOptions {
    maxDurationSeconds: number;
    onChange?: (file: File | null) => void;
}

interface IRecordAudioWaveState {
    waveRef: RefObject<HTMLDivElement>;
    status: TRecordStatus;
    isPlaying: boolean;
    playbackSeconds: number;
    durationSeconds: number;
    elapsedSeconds: number;
    hasWave: boolean;
    micUnavailable: boolean;
    togglePlay: () => void;
    startRecording: () => Promise<void>;
    stopRecording: () => void;
}

const canUseMicrophone = (): boolean => {
    return typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
};

const pickMimeType = (): string => {
    if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
        return '';
    }
    return MIME_CANDIDATES.find(type => MediaRecorder.isTypeSupported(type)) || '';
};

const extensionForMime = (mime: string): string => {
    const base = mime.split(';')[0];
    if (base === 'audio/mp4') {
        return 'm4a';
    }
    if (base === 'audio/ogg') {
        return 'ogg';
    }
    if (base === 'audio/wav' || base === 'audio/x-wav') {
        return 'wav';
    }
    if (base === 'audio/mpeg' || base === 'audio/mp3') {
        return 'mp3';
    }
    return 'webm';
};

const useMicrophoneAvailability = (
    stopRecordingRef: MutableRefObject<() => void>,
): [boolean, (unavailable: boolean) => void] => {
    const [micUnavailable, setMicUnavailable] = useState(!canUseMicrophone());

    useEffect(() => {
        if (!canUseMicrophone()) {
            setMicUnavailable(true);
            return undefined;
        }
        let permissionStatus: PermissionStatus | null = null;
        const applyPermission = (): void => {
            if (!permissionStatus) {
                return;
            }
            setMicUnavailable(permissionStatus.state === 'denied');
        };
        if (navigator.permissions?.query) {
            navigator.permissions.query({ name: 'microphone' } as unknown as PermissionDescriptor)
                .then(result => {
                    permissionStatus = result;
                    // A denial from an earlier visit must not block the next time the popup opens.
                    result.onchange = applyPermission;
                })
                .catch(() => undefined);
        }
        return () => {
            if (permissionStatus) {
                permissionStatus.onchange = null;
            }
        };
    }, []);

    useEffect(() => {
        if (micUnavailable) {
            stopRecordingRef.current();
        }
    }, [micUnavailable, stopRecordingRef]);

    return [micUnavailable, setMicUnavailable];
};

const useRecordAudioWave = ({
    maxDurationSeconds,
    onChange,
}: IRecordAudioWaveOptions): IRecordAudioWaveState => {
    const releaseRecorderRef = useRef<() => void>(() => undefined);
    const player = useWaveSurfer({
        maxDurationSeconds: maxDurationSeconds,
        beforeDestroy: () => releaseRecorderRef.current(),
    });
    const [status, setStatus] = useState<TRecordStatus>('idle');
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const onChangeRef = useRef(onChange);
    const startingRef = useRef(false);
    const statusRef = useRef<TRecordStatus>('idle');
    const recordRef = useRef<RecordPlugin | null>(null);
    const discardRecordingRef = useRef(false);
    const stopTimerRef = useRef<number>(0);
    const playbackTimerRef = useRef<number>(0);
    const maxDurationRef = useRef(maxDurationSeconds);
    const { mountedRef, setDurationSeconds, setHasWave, recreate } = player;

    onChangeRef.current = onChange;
    maxDurationRef.current = maxDurationSeconds;

    const shownSeconds = (totalSeconds: number): number => {
        if (totalSeconds >= maxDurationRef.current) {
            return maxDurationRef.current;
        }
        return Math.floor(totalSeconds);
    };

    const clearStopTimer = (): void => {
        window.clearTimeout(stopTimerRef.current);
        stopTimerRef.current = 0;
    };

    const setRecordStatus = (next: TRecordStatus): void => {
        statusRef.current = next;
        setStatus(next);
    };

    const stopRecording = (): void => {
        const record = recordRef.current;
        if (!record?.isRecording()) {
            return;
        }
        const elapsed = shownSeconds(record.getDuration() / 1000);
        setElapsedSeconds(elapsed);
        setDurationSeconds(elapsed);
        clearStopTimer();
        record.stopRecording();
    };

    const stopRecordingRef = useRef(stopRecording);
    stopRecordingRef.current = stopRecording;

    const [micUnavailable, setMicUnavailable] = useMicrophoneAvailability(stopRecordingRef);

    const playRecordedClip = (blob: Blob): void => {
        window.clearTimeout(playbackTimerRef.current);
        playbackTimerRef.current = window.setTimeout(() => {
            if (!mountedRef.current || statusRef.current !== 'recorded') {
                return;
            }
            const playback = recreate();
            if (!playback) {
                return;
            }
            playback.toggleInteraction(true);
            playback.loadBlob(blob).catch(() => {
                if (mountedRef.current) {
                    setHasWave(false);
                }
            });
        }, 0);
    };

    const attachRecorder = (): RecordPlugin | null => {
        const ws = recreate();
        if (!ws) {
            return null;
        }
        const mimeType = pickMimeType();
        const record = ws.registerPlugin(RecordPlugin.create({
            continuousWaveform: true,
            continuousWaveformDuration: maxDurationRef.current,
            renderRecordedAudio: false,
            scrollingWaveform: false,
            mimeType: mimeType || undefined,
        }));
        recordRef.current = record;
        record.on('record-end', (blob: Blob) => {
            clearStopTimer();
            if (discardRecordingRef.current || !mountedRef.current) {
                return;
            }
            if (!blob.size) {
                setRecordStatus('idle');
                setElapsedSeconds(0);
                setHasWave(false);
                return;
            }
            const baseType = (blob.type || 'audio/webm').split(';')[0];
            const file = new File(
                [blob],
                `name-pronunciation.${extensionForMime(baseType)}`,
                { type: baseType }
            );
            setDurationSeconds(shownSeconds(record.getDuration() / 1000));
            setRecordStatus('recorded');
            setHasWave(true);
            onChangeRef.current?.(file);
            playRecordedClip(blob);
        });
        record.on('record-progress', (elapsedMs: number) => {
            const seconds = elapsedMs / 1000;
            if (seconds >= maxDurationRef.current) {
                stopRecordingRef.current();
                return;
            }
            const shown = shownSeconds(seconds);
            setElapsedSeconds(previous => previous === shown ? previous : shown);
        });
        return record;
    };

    const releaseRecorder = (discard: boolean): void => {
        window.clearTimeout(playbackTimerRef.current);
        clearStopTimer();
        const record = recordRef.current;
        if (discard) {
            discardRecordingRef.current = true;
        }
        if (!record) {
            return;
        }
        if (record.isRecording()) {
            record.stopRecording();
        }
        record.stopMic();
    };
    releaseRecorderRef.current = () => releaseRecorder(!mountedRef.current);

    useEffect(() => () => {
        releaseRecorder(true);
    }, []);

    const startRecording = async (): Promise<void> => {
        if (startingRef.current || statusRef.current === 'recording' || !canUseMicrophone()) {
            return;
        }
        startingRef.current = true;
        try {
            const probe = await navigator.mediaDevices.getUserMedia({ audio: true });
            probe.getTracks().forEach(track => track.stop());
        } catch {
            // Show the failure only while this recorder stays open. The next open asks again.
            if (mountedRef.current) {
                setMicUnavailable(true);
            }
            startingRef.current = false;
            return;
        }
        if (!mountedRef.current) {
            startingRef.current = false;
            return;
        }
        try {
            window.clearTimeout(playbackTimerRef.current);
            const record = attachRecorder();
            if (!record) {
                return;
            }
            setElapsedSeconds(0);
            onChangeRef.current?.(null);
            setMicUnavailable(false);
            await record.startRecording();
            if (!mountedRef.current) {
                record.stopMic();
                return;
            }
            setRecordStatus('recording');
            setHasWave(true);
            clearStopTimer();
            stopTimerRef.current = window.setTimeout(
                () => stopRecordingRef.current(),
                maxDurationRef.current * 1000
            );
        } catch {
            if (mountedRef.current) {
                setMicUnavailable(true);
                setRecordStatus('idle');
                setHasWave(false);
            }
        } finally {
            startingRef.current = false;
        }
    };

    return {
        waveRef: player.waveRef,
        status: status,
        isPlaying: player.isPlaying,
        playbackSeconds: player.playbackSeconds,
        durationSeconds: player.durationSeconds,
        elapsedSeconds: elapsedSeconds,
        hasWave: player.hasWave,
        micUnavailable: micUnavailable,
        togglePlay: player.togglePlay,
        startRecording: startRecording,
        stopRecording: stopRecording,
    };
};

export { useRecordAudioWave, TRecordStatus };
